import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, Loader2, Pencil, Plus, Star, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { blogApi, messageOf, type PostSummary } from '@/services/blogApi'

export default function BlogPage() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<PostSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [configured, setConfigured] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const config = await blogApi.config()
      setConfigured(config.configured)
      if (!config.configured) {
        setPosts([])
        return
      }
      setPosts(await blogApi.list())
    } catch (caught) {
      setError(messageOf(caught, 'Could not load posts'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const remove = async (post: PostSummary) => {
    if (
      !window.confirm(
        `Remove "${post.title ?? post.slug}"? The page disappears from the site with the next production release.`
      )
    ) {
      return
    }
    try {
      const result = await blogApi.remove(post.slug)
      toast.success(result.message)
      void load()
    } catch (caught) {
      toast.error(messageOf(caught, 'Could not remove the post'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Blog</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Posts are committed to the website repository and go live with the next production
            release.
          </p>
        </div>
        <Button onClick={() => navigate('/admin/blog/new')} disabled={!configured}>
          <Plus className="mr-1.5 h-4 w-4" /> New post
        </Button>
      </div>

      {!configured && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Blog publishing is not configured on the server. An engineer needs to set{' '}
            <code className="font-mono">BLOG_GITHUB_TOKEN</code> before posts can be listed or
            published.
          </span>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading posts…
        </div>
      ) : posts.length === 0 && configured && !error ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-500">
          No posts yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Author</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.map(post => (
                <tr key={post.slug} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {post.featured && (
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                      )}
                      <span className="font-medium">{post.title ?? post.slug}</span>
                      {/* A post already in the repo can be hand-edited into an
                          unparseable state; it would fail the build, so say so. */}
                      {!post.valid && (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700"
                          title={post.error}
                        >
                          <AlertTriangle className="h-3 w-3" /> Broken
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-gray-400">/blog/{post.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{post.category ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{post.author ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{post.date ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                        onClick={() => navigate(`/admin/blog/${post.slug}`)}
                        aria-label={`Edit ${post.slug}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                        onClick={() => remove(post)}
                        aria-label={`Remove ${post.slug}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
